import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { AssetForm } from './AssetForm';

import { useCreateAssetMutation } from '~/modules/transportation/hooks/mutations/useCreateAssetMutation';
import {
  $assetForm,
  $updateAssetForm,
} from '~/modules/transportation/state/assetForm.localState';
import { AssetFormState } from '~/modules/transportation/types';

type AssetDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  asset?: AssetFormState;
};

export const AssetDialog = ({
  isOpen,
  onClose,
  onSuccess,
  asset,
}: AssetDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const form = useAtomValue($assetForm);
  const updateForm = useSetAtom($updateAssetForm);
  const { mutateAsync: createAsset } = useCreateAssetMutation();

  React.useEffect(() => {
    if (asset) {
      updateForm({
        identifier: asset.identifier,
        typeId: asset.typeId,
        description: asset.description,
        status: asset.status,
        fillState: asset.fillState,
        customAttributes: asset.customAttributes,
        tareWeight: asset.tareWeight,
      });
    }
  }, [asset, updateForm]);

  const handleClose = () => {
    onClose();
    // Reset form when closing
    updateForm({
      identifier: '',
      typeId: '',
      description: '',
      status: form.status,
      fillState: form.fillState,
      customAttributes: {},
      tareWeight: null,
    });
  };

  const handleSave = async () => {
    try {
      if (asset) {
        toast.success(t.assetsView.form.messages.updateSuccess);
      } else {
        await createAsset({
          identifier: form.identifier,
          typeId: form.typeId,
          description: form.description,
          status: form.status,
          fillState: form.fillState,
          customAttributes: form.customAttributes,
          tareWeight: form.tareWeight,
        });
        toast.success(t.assetsView.form.messages.createSuccess);
      }
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.assetsView.form.messages.error,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@AssetDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {asset
              ? t.assetsView.form.title.edit
              : t.assetsView.form.title.create}
          </div>
        </DialogHeader>

        <div className="p-4">
          <AssetForm isEditing={!!asset} />
        </div>

        <DialogFooter>
          <Button onPress={handleClose}>
            {t.assetsView.form.buttons.cancel}
          </Button>
          <Button color="primary" variant="solid" onPress={handleSave}>
            {asset
              ? t.assetsView.form.buttons.save
              : t.assetsView.form.buttons.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
